import type { Currency, RevenueReport } from '@witmetrics/utils';
import { isNullish } from '@witmetrics/utils/misc';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '@witmetrics/utils/strings';
import { filterNullish } from '@witmetrics/utils/arrays';
import { isFuture } from '@witmetrics/utils/time';
import { ACCENT_COLORS } from '@/constants/colors';
import { renderMonth } from '@/utils/time';
import type { ChartProps } from '@/charts/Chart';
import {
  BAR_GRADIENT,
  CHIP_HEADROOM,
  CHIP_OFFSET,
  DATA_LABEL_CHIP,
} from '@/charts/Chart/constants';
import { buildBarGradientFill } from '@/charts/Chart/utils';
import type { AxisBounds, GrowthPoint } from './types';
import {
  BAR_RADIUS,
  CHIP_CLEARANCE,
  COLUMN_WIDTH,
  GROWTH_STEPS,
  LINE_WIDTH,
  REVENUE_STEPS,
  TICK_COUNT,
} from './constants';

export const styles = {
  colors: [BAR_GRADIENT.brand.bottom, ACCENT_COLORS.teal],
  fill: buildBarGradientFill([BAR_GRADIENT.brand], [ACCENT_COLORS.teal]),
  growthChip: {
    borderColor: 'rgba(70, 60, 50, 0.1)',
    borderRadius: 6,
    borderWidth: 1,
    style: {
      fontSize: '10.5px',
      fontWeight: 700,
      background: '#ffffff',
      // Asymmetric on purpose: the text box includes descender space and % side bearing
      padding: { left: 6, right: 4, top: 2, bottom: 4 },
    },
  },
  markers: {
    size: [0, 4],
    colors: ['#ffffff'],
    strokeColors: ACCENT_COLORS.teal,
    strokeWidth: 2,
    hover: { size: 5 },
  },
  grid: { borderColor: 'rgba(70, 60, 50, 0.055)' },
};

function formatAxisPrice(value: number, currency?: Currency) {
  if (Math.abs(value) < 1000) {
    return formatPrice(value, currency, { useRounding: true });
  }
  return `${formatCurrency(currency)}${formatNumber(value / 1000, 0)}k`;
}

function getGrowthColor(value: number) {
  if (value === 0) return ACCENT_COLORS.taupe;
  return value > 0 ? ACCENT_COLORS.positive : ACCENT_COLORS.negative;
}

export function buildRevenueBounds(values: (number | null)[]): AxisBounds {
  const max = Math.max(...filterNullish(values), 0);
  const step =
    REVENUE_STEPS.find((s) => s * TICK_COUNT >= max * CHIP_HEADROOM) ??
    REVENUE_STEPS[REVENUE_STEPS.length - 1];
  return { min: 0, max: step * TICK_COUNT };
}

export function buildGrowthBounds(values: (number | null)[]): AxisBounds {
  const present = filterNullish(values);
  const high = Math.max(...present, 0);
  const low = Math.min(...present, 0);
  const step =
    GROWTH_STEPS.find(
      (s) => Math.ceil(high / s) - Math.floor(low / s) <= TICK_COUNT
    ) ?? GROWTH_STEPS[GROWTH_STEPS.length - 1];
  const min = Math.floor(low / step) * step;
  const max = Math.ceil(high / step) * step;
  const missing = TICK_COUNT - Math.round((max - min) / step);
  return { min, max: max + missing * step };
}

function toPlotFraction({ min, max }: AxisBounds, value: number) {
  return (max - value) / (max - min);
}

export function buildGrowthPoints(
  categories: string[],
  revenue: (number | null)[],
  growth: (number | null)[],
  revenueBounds: AxisBounds,
  growthBounds: AxisBounds
): GrowthPoint[] {
  return categories.flatMap((category, i) => {
    const value = growth[i];
    if (isNullish(value)) return [];
    const barTop = toPlotFraction(revenueBounds, revenue[i] ?? 0);
    const point = toPlotFraction(growthBounds, value);
    const isClashing = Math.abs(barTop - point) < CHIP_CLEARANCE;
    return [{ category, value, flipBelow: isClashing && point < barTop }];
  });
}

function getCurrentRevenue(report: RevenueReport, index: number) {
  const period = report.data[index];
  if (!period || isFuture(period.startDate)) return null;
  return period.leads.revenue.value;
}

export function formatReport(
  currentReport: RevenueReport,
  previousReport: RevenueReport,
  currency?: Currency
): ChartProps {
  const revenueLabel = (val: number) => formatAxisPrice(val, currency);
  const growthLabel = (val: number) => formatPercentage(val, 1);
  const categories = previousReport.data.map((d) => renderMonth(d.startDate));
  const revenue = previousReport.data.map((_, i) => {
    const current = getCurrentRevenue(currentReport, i);
    return isNullish(current) ? null : Math.round(current);
  });
  const growth = previousReport.data.map((d, i) => {
    const current = getCurrentRevenue(currentReport, i);
    const previous = d.leads.revenue.value;
    if (isNullish(current) || isNullish(previous) || previous === 0) {
      return null;
    }
    return (current - previous) / previous;
  });
  const revenueBounds = buildRevenueBounds(revenue);
  const growthBounds = buildGrowthBounds(growth);
  const growthPoints = buildGrowthPoints(
    categories,
    revenue,
    growth,
    revenueBounds,
    growthBounds
  );
  return {
    series: [
      { name: 'Revenue', type: 'column', data: revenue },
      { name: 'YoY growth', type: 'line', data: growth },
    ],
    options: {
      colors: styles.colors,
      fill: styles.fill,
      plotOptions: {
        bar: {
          columnWidth: COLUMN_WIDTH,
          borderRadius: BAR_RADIUS,
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        ...DATA_LABEL_CHIP,
        enabledOnSeries: [0],
        // ApexCharts still calls the formatter for null points, so blank them
        formatter: (
          val: number,
          { dataPointIndex }: { dataPointIndex: number }
        ) => (isNullish(revenue[dataPointIndex]) ? '' : revenueLabel(val)),
      },
      annotations: {
        points: growthPoints.map(({ category, value, flipBelow }) => ({
          x: category,
          y: value,
          yAxisIndex: 1,
          seriesIndex: 1,
          marker: { size: 0 },
          label: {
            ...styles.growthChip,
            text: growthLabel(value),
            offsetY: flipBelow ? CHIP_OFFSET * 2 : 0,
            style: { ...styles.growthChip.style, color: getGrowthColor(value) },
          },
        })),
      },
      stroke: { width: [0, LINE_WIDTH], curve: 'straight' },
      markers: styles.markers,
      grid: styles.grid,
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          { formatter: (val: number) => formatPrice(val, currency) },
          { formatter: growthLabel },
        ],
      },
      xaxis: { categories },
      yaxis: [
        {
          ...revenueBounds,
          tickAmount: TICK_COUNT,
          title: { text: 'Revenue' },
          labels: { formatter: revenueLabel },
        },
        {
          ...growthBounds,
          opposite: true,
          tickAmount: TICK_COUNT,
          title: { text: 'YoY Growth' },
          labels: { formatter: (val: number) => formatPercentage(val, 0) },
        },
      ],
    },
  };
}
